// Phase 18 · Community · Post detail
import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Flag, Hash, Eye } from "lucide-react";
import VoteButtons from "../../components/community/VoteButtons";
import MarketplaceEmbedCard from "../../components/community/MarketplaceEmbedCard";
import MiniLandingEmbedCard from "../../components/community/MiniLandingEmbedCard";
import VideoEmbed from "../../components/community/VideoEmbed";
import CommentThread from "../../components/community/CommentThread";
import ReportModal from "../../components/community/ReportModal";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PostDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newComment, setNewComment] = useState("");
    const [busy, setBusy] = useState(false);
    const [showReport, setShowReport] = useState(false);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [pr, cr] = await Promise.all([
                fetch(`${API}/api/community/posts/${id}`, { credentials: "include" }),
                fetch(`${API}/api/community/posts/${id}/comments`, { credentials: "include" }),
            ]);
            if (pr.ok) setPost(await pr.json());
            if (cr.ok) {
                const d = await cr.json();
                setComments(d.items || []);
            }
        } catch {}
        setLoading(false);
    }, [id]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const submitComment = async () => {
        if (!newComment.trim()) return;
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/posts/${id}/comments`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ body: newComment }),
            });
            if (!r.ok) {
                toast.error(r.status === 429 ? "Comment rate limit" : "Comment failed");
                return;
            }
            setNewComment("");
            loadAll();
        } catch { toast.error("Network error"); }
        finally { setBusy(false); }
    };

    if (loading) return (
        <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
            <p className="text-neutral-500 text-sm">Loading…</p>
        </div>
    );
    if (!post) return (
        <div className="min-h-screen bg-[#0A0A0A] text-neutral-200 flex flex-col items-center justify-center gap-3">
            <p>Post not found</p>
            <Link to="/community" className="text-[#FFFFFF] hover:text-[#CCCCCC] text-sm">← Back to community</Link>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter" data-testid="post-detail-page">
            <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0A0A0A]/80 border-b border-[#1A1A1A]">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link
                        to="/community"
                        className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm"
                    >
                        <ArrowLeft size={16} /> Back
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <a href="/" className="text-sm font-bold bg-white bg-clip-text text-transparent">
                        Global Lead Suite
                    </a>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 py-6">
                <article className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 mb-6">
                    <div className="flex gap-4">
                        <VoteButtons targetType="post" targetId={post.id} initial={post} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-xs text-neutral-500 mb-2">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1A1A1A]">
                                    {post.post_type.replace("_", " ")}
                                </span>
                                <Link to={`/community/${post.category_slug}`} className="inline-flex items-center gap-0.5 hover:text-[#FFFFFF]">
                                    <Hash size={10} />{post.category_slug}
                                </Link>
                                <span>·</span>
                                <Link to={`/profile/${post.author_username}`} className="hover:text-[#CCCCCC]">
                                    @{post.author_username}
                                </Link>
                                <span>·</span>
                                <span className="inline-flex items-center gap-1"><Eye size={11} />{post.view_count || 0}</span>
                            </div>
                            <h1 className="text-2xl font-bold text-neutral-100 leading-tight mb-3" data-testid="post-title">
                                {post.title}
                            </h1>
                            {post.body && (
                                <p className="text-neutral-300 whitespace-pre-wrap leading-relaxed" data-testid="post-body">
                                    {post.body}
                                </p>
                            )}
                            {post.post_type === "asset_marketplace" && post.marketplace_preview && (
                                <div className="mt-4">
                                    <MarketplaceEmbedCard preview={post.marketplace_preview} />
                                </div>
                            )}
                            {post.post_type === "mini_landing" && post.mini_landing_preview && (
                                <div className="mt-4">
                                    <MiniLandingEmbedCard preview={post.mini_landing_preview} />
                                </div>
                            )}
                            {(post.post_type === "video" || post.post_type === "embed") && post.embed_url && (
                                <div className="mt-4"><VideoEmbed url={post.embed_url} /></div>
                            )}
                            {user && (
                                <div className="mt-4 pt-3 border-t border-[#1A1A1A] flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowReport(true)}
                                        className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-[#FAFAFA] transition-colors"
                                        data-testid="post-report-btn"
                                    >
                                        <Flag size={13} /> Report
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </article>

                <section className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6">
                    <h2 className="text-sm font-semibold text-neutral-200 mb-4 flex items-center gap-2">
                        <MessageCircle size={16} className="text-[#FFFFFF]" />
                        {post.comment_count || 0} Comments
                    </h2>
                    {user ? (
                        <div className="mb-6" data-testid="comment-form">
                            <textarea
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                rows={3}
                                placeholder="What are your thoughts?"
                                className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                                data-testid="comment-new-input"
                            />
                            <div className="flex justify-end mt-2">
                                <button
                                    type="button"
                                    onClick={submitComment}
                                    disabled={busy || !newComment.trim()}
                                    className="bg-white text-neutral-900 font-semibold px-4 py-1.5 rounded-lg text-sm disabled:opacity-40"
                                    data-testid="comment-new-submit"
                                >
                                    {busy ? "..." : "Comment"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p className="text-xs text-neutral-500 italic mb-4">
                            <a href="/" className="text-[#FFFFFF] hover:text-[#CCCCCC]">Sign in</a> to comment.
                        </p>
                    )}
                    <CommentThread postId={post.id} items={comments} onChange={loadAll} />
                </section>
            </main>

            {showReport && <ReportModal postId={post.id} onClose={() => setShowReport(false)} />}
        </div>
    );
}
